// Beacon runtime configuration. Read from a <script> tag's data-* attributes
// or from window.CAPI_CONFIG, in that order of precedence.

export interface BeaconConfig {
  pixel_id: string;
  api_base: string;        // e.g. "https://capi.example.com"
  page?: 'pageview' | 'confirmation';
  source?: 'stealthseminar' | 'clickfunnels';
  event_name?: 'Lead' | 'Purchase';
  // Optional override for the cookie domain. Empty string = host-only.
  // Omit to auto-resolve the registrable root via probing.
  cookie_domain?: string;
  // Conversion-fire fallbacks — used when the page-side extractor can't
  // find a purchase value/currency in the DOM. CF's order-complete
  // template clears order data after the redirect render, so a static
  // fallback is necessary for the browser pixel to carry value.
  fallback_value?: number;
  fallback_currency?: string;
}

declare global {
  interface Window {
    CAPI_CONFIG?: Partial<BeaconConfig>;
    CAPI_CONFIGS?: Array<Partial<BeaconConfig>>;
  }
}

// document.currentScript is unreliable: it returns null when the <script>
// tag was dynamically injected by another script (common with SPA / Vite
// shells that re-inject 3rd-party tags). Multiple beacon tags also share
// the same src.
//
// Strategy: walk all beacon <script> tags in document order, claim the
// first un-claimed one by stamping a data-* flag. Bundle IIFEs execute
// synchronously in document order, so each execution claims its own tag.
function claimThisScript(): HTMLScriptElement | null {
  if (typeof document === 'undefined') return null;

  // Prefer the live currentScript when it actually works.
  const live = document.currentScript as HTMLScriptElement | null;
  if (live && !live.dataset.capiClaimed) {
    live.dataset.capiClaimed = '1';
    return live;
  }

  // Match any beacon-related script: data-pixel-id (when attrs survive),
  // or any /beacon.js src (when the host platform strips attrs but keeps
  // the script URL — StealthSeminar, etc.).
  const candidates = document.querySelectorAll<HTMLScriptElement>(
    'script[data-pixel-id], script[src*="beacon.js"]'
  );
  for (const el of Array.from(candidates)) {
    if (el.dataset.capiClaimed) continue;
    el.dataset.capiClaimed = '1';
    return el;
  }
  return null;
}

// Parse config out of the script tag's src URL query string. This is the
// most resilient delivery path — platforms that strip data-* attrs and
// inline <script> blocks generally still preserve the script URL intact.
function readUrlConfig(el: HTMLScriptElement | null): Partial<BeaconConfig> {
  if (!el || !el.src) return {};
  let u: URL;
  try { u = new URL(el.src); } catch { return {}; }

  const out: Partial<BeaconConfig> = {};
  // Default api_base to the script's origin — saves repeating it.
  out.api_base = u.origin;

  const p = u.searchParams;
  const pixel = p.get('pixel_id') ?? p.get('pid');
  if (pixel) out.pixel_id = pixel;

  const apiBase = p.get('api_base') ?? p.get('api');
  if (apiBase) out.api_base = apiBase;

  const page = p.get('page');
  if (page === 'pageview' || page === 'confirmation') out.page = page;

  const source = p.get('source');
  if (source === 'stealthseminar' || source === 'clickfunnels') out.source = source;

  const ev = p.get('event_name') ?? p.get('ev');
  if (ev === 'Lead' || ev === 'Purchase') out.event_name = ev;

  const cd = p.get('cookie_domain');
  if (cd !== null) out.cookie_domain = cd;

  const fv = p.get('fallback_value');
  if (fv !== null) {
    const n = Number(fv);
    if (Number.isFinite(n)) out.fallback_value = n;
  }
  const fc = p.get('fallback_currency');
  if (fc) out.fallback_currency = fc;

  return out;
}

const SCRIPT_AT_LOAD = claimThisScript();

function readScriptAttrs(): Partial<BeaconConfig> {
  const el = SCRIPT_AT_LOAD
    ?? (document.querySelector('script[data-capi-beacon]') as HTMLScriptElement | null);
  if (!el) return {};
  const out: Partial<BeaconConfig> = {};
  if (el.dataset.pixelId)      out.pixel_id      = el.dataset.pixelId;
  if (el.dataset.apiBase)      out.api_base      = el.dataset.apiBase;
  if (el.dataset.page)         out.page          = el.dataset.page as BeaconConfig['page'];
  if (el.dataset.source)       out.source        = el.dataset.source as BeaconConfig['source'];
  if (el.dataset.eventName)    out.event_name    = el.dataset.eventName as BeaconConfig['event_name'];
  if (el.dataset.cookieDomain !== undefined) out.cookie_domain = el.dataset.cookieDomain;
  return out;
}

// Pull the next config off the global CAPI_CONFIGS queue. This is the most
// resilient delivery mechanism: pages set:
//
//   <script>
//     (window.CAPI_CONFIGS = window.CAPI_CONFIGS || []).push({
//       pixel_id: '...', api_base: '...', page: 'pageview'
//     });
//   </script>
//   <script src=".../beacon.js" defer></script>
//
// Each beacon execution shifts one config off the front of the queue, so
// multiple beacon instances on the same page get their own configs in
// document order. Inline <script> blocks survive platform sanitization
// (e.g. StealthSeminar) better than data-* attrs on script tags.
function shiftQueueConfig(): Partial<BeaconConfig> | null {
  const q = window.CAPI_CONFIGS;
  if (Array.isArray(q) && q.length) return q.shift() ?? null;
  return null;
}

export function readConfig(): BeaconConfig {
  const queueCfg = shiftQueueConfig() ?? {};
  const attrs = readScriptAttrs();
  const urlCfg = readUrlConfig(SCRIPT_AT_LOAD);
  const win = window.CAPI_CONFIG ?? {};
  // Precedence (last write wins): window singleton < data-* attrs <
  // CAPI_CONFIGS queue < script src URL params. URL wins because it's the
  // most resilient delivery channel — survives SPA platforms (SS) that
  // strip everything except the script URL itself.
  const merged = { ...win, ...attrs, ...queueCfg, ...urlCfg };
  if (!merged.pixel_id) throw new Error('CAPI beacon: missing pixel_id');
  if (!merged.api_base) throw new Error('CAPI beacon: missing api_base');
  return merged as BeaconConfig;
}
